import * as React from 'react'
import { Link } from 'react-router-dom'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'
import { faPenAlt } from '@fortawesome/pro-light-svg-icons'

import { Button, Buttons } from '@espoon-voltti/react-components/elements'

import { View, Section } from '@espoon-voltti/react-components/layout'
import { Collapsible } from '@espoon-voltti/react-voltti/components/collapsible'

import { GrantPeriod } from '@espoon-voltti/nuora-domain/grants'

import { PublishedApp } from '../../../domain'
import { Breadcrumb } from '../../../components'
import { t } from '../../../i18n'
import { localizedPathnames } from '../../../utils/localized-pathname'

import { ProcessSteps } from '../partials'
import { Signers } from './signers'
import { maybeProjectGrantForm, maybeGeneralGrantForm } from '../support'
import { ProjectGrantAppPreview } from './project-grant'
import { GeneralGrantAppPreview } from './general-grant'

const getDisplayName = (x: PublishedApp) => {
  switch (x.form.type) {
    case 'PROJECT_GRANT':
    case 'PROJECT_GRANT_PAPER':
      return x.form.event.name
    case 'GENERAL_GRANT':
    case 'GENERAL_GRANT_PAPER':
      return t(['views', 'generalGrant', 'form', 'title'])
    default:
      const _: never = x.form
      return _
  }
}

interface AppPreviewProps {
  app: PublishedApp
  grantPeriod: GrantPeriod
}

export const AppPreview: React.FC<AppPreviewProps> = (p) => {
  return (
    <View
      dataQa="appPreview"
      title={getDisplayName(p.app) ?? ''}
      subtitle={p.app.applicant.org.name}
      status={t(['common', 'statuses', p.app.state])}
      breadcrumbs={
        <Breadcrumb
          route={[
            {
              label: t(['breadcrumb', 'home']),
              link: localizedPathnames.home(p.app.applicant.org.id)
            },
            { label: t(['breadcrumb', p.grantPeriod.type]), link: '#' }
          ]}
        />
      }
    >
      <ProcessSteps app={p.app} />

      {pipe(
        p.app.form,
        maybeProjectGrantForm,
        O.fold(
          () =>
            pipe(
              p.app.form,
              maybeGeneralGrantForm,
              O.map((x) => (
                <GeneralGrantAppPreview
                  key={p.app.id}
                  applicant={p.app.applicant}
                  form={x}
                  preReport={maybePreReport(p.app)}
                />
              )),
              O.toNullable
            ),
          (x) => (
            <ProjectGrantAppPreview
              key={p.app.id}
              applicant={p.app.applicant}
              form={x}
              preReport={maybePreReport(p.app)}
            />
          )
        )
      )}
      <Section>
        <Collapsible
          dataQa={'signing'}
          title={t(['views', 'form', 'signing', 'title'])}
          icon={faPenAlt}
          open={true}
        >
          <Signers
            representatives={p.app.applicant.org.representatives}
            signers={p.app.signing.signers}
          />
        </Collapsible>
      </Section>

      <Section>
        <Buttons centered>
          <Link to={localizedPathnames.home(p.app.applicant.org.id)}>
            <Button width={'wide'} dataQa={'close'}>
              {t(['views', 'preview', 'close'])}
            </Button>
          </Link>
        </Buttons>
      </Section>
    </View>
  )
}

const maybePreReport = (x: PublishedApp) => {
  switch (x.state) {
    case 'PAID':
    case 'PAYMENT_WAIT':
    case 'REJECTED':
      return O.fromNullable(x.preReport)
    case 'PRE_REPORT':
    case 'RECOVERY':
    case 'POST_REPORT':
    case 'RECOVERED':
    case 'REVIEW':
    case 'SENT':
      return O.none
    default:
      const _: never = x
      return _
  }
}
